// S5 · Step 3 — the People panel (Module 02 §4 S5: "invite grid + `people`
// import shortcut"), restyled onto the design-system primitives (UX-1): a Card
// with a Field-grammar invite form and the invite grid as a `DataTable` (the
// §3.3 collapse contract comes with it).
//
// It feeds three go-live checks worth 14 of the 119 weight — `people.admin_per_
// facility` (6), `people.floor_workers_min` (5), `people.invites_ready` (3),
// §3.6.
//
// Contract (Module 16): `GET /v1/invites`, `POST /v1/invites`,
// `POST /v1/invites/{id}/revoke|resend`, plus `GET /v1/roles` because an invite
// is keyed by `role_id`. The bulk path is the `people` import lane.
//
// §7 / Module 16: inviting needs `identity.invite_member`. The client cannot
// derive that from the onboarding grant table (it is another module's code), so
// the panel reads the ONE signal it can trust — the API's own 403 — and degrades
// to a read-only grid rather than guessing.

import { enumLabel } from "../../../../lib/enum-label";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import type { components } from "@assessly/api-client";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { EmptyState, ErrorState, TableSkeleton } from "../../../../components/states";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { formatTimestamp } from "../../../../lib/datetime";
import { roleLabel } from "../../../../lib/role-label";
import {
  useCreateInvite,
  useInvites,
  useResendInvite,
  useRevokeInvite,
  useRoles,
} from "../../hooks/onboarding-queries";
import { useTerm } from "../../hooks/terminology";
import { WizardDialog } from "../../components/wizard-dialog";
import { WizardProblemNotice } from "../../components/wizard-notices";
import type { ExistingTree } from "./plant-draft";

type S = components["schemas"];
type InviteRow = S["Invite"];

export interface PeoplePanelProps {
  tree: ExistingTree;
  frozen: boolean;
  /** §7 mirror for the `people` import lane shortcut. */
  canImportPeople: boolean;
}

type ScopeKind = "group" | "facility";

export function PeoplePanel({ tree, frozen, canImportPeople }: PeoplePanelProps) {
  const { t, i18n } = useTranslation();
  const { term } = useTerm();
  const invites = useInvites({ status: "pending" });
  const roles = useRoles();
  const create = useCreateInvite();
  const revoke = useRevokeInvite();
  const resend = useResendInvite();

  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState("");
  const [scope, setScope] = useState<ScopeKind>("group");
  const [facilityId, setFacilityId] = useState("");
  const [revoking, setRevoking] = useState<{ id: string; email: string } | null>(null);

  const facilities = tree.facilities.filter((f) => f.status === "active");
  // The list read and the roles read share the same `identity.*` permission, so
  // a 403 on either means this caller may not administer people. Read from the
  // server's answer, never guessed from a role code of another module.
  const forbidden =
    isForbidden(invites.error) || isForbidden(roles.error) || isForbidden(create.error);
  // Invites are Module 16's, not the wizard's: a live tenant still hires
  // people, and this panel is the only screen that sends an invite, so the
  // wizard's go-live freeze leaves it open (spec 02 S5, D-434). `frozen`
  // still decides the bulk-import hint, which writes through the wizard.
  const canInvite = !forbidden;
  const items = invites.data?.items ?? [];
  const roleOptions = roles.data?.items ?? [];

  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  const scopeOk = scope === "group" || facilityId.length > 0;
  const canSubmit = canInvite && emailOk && roleId.length > 0 && scopeOk && !create.isPending;

  const columns: readonly DataTableColumn<InviteRow>[] = [
    {
      key: "email",
      header: t("onboarding.plant.people.colEmail"),
      id: true,
      render: (row) => row.email,
    },
    {
      key: "scope",
      header: t("onboarding.plant.people.colScope"),
      render: (row) => enumLabel(t, "onboarding.plant.people.scope", row.scope_kind),
    },
    {
      key: "expires",
      header: t("onboarding.plant.people.colExpires"),
      age: true,
      render: (row) => formatTimestamp(row.expires_at, i18n.language),
    },
    {
      key: "actions",
      header: t("onboarding.plant.people.colActions"),
      actions: true,
      render: (row) =>
        canInvite ? (
          <span className="plant-invite-actions">
            <button
              type="button"
              className="ds-lnk"
              disabled={resend.isPending || row.resent_count >= 5}
              title={row.resent_count >= 5 ? t("onboarding.plant.people.resendCapped") : undefined}
              onClick={() => {
                resend.mutate({ id: row.id });
              }}
            >
              {t("onboarding.plant.people.resend")}
            </button>
            <button
              type="button"
              className="ds-lnk"
              disabled={revoke.isPending}
              onClick={() => {
                setRevoking({ id: row.id, email: row.email });
              }}
            >
              {t("onboarding.plant.people.revoke")}
            </button>
          </span>
        ) : null,
    },
  ];

  return (
    <Card
      title={t("onboarding.plant.people.heading")}
      count={items.length > 0 ? items.length : undefined}
      className="plant-people"
      data-testid="plant-people"
    >
      <div className="plant-people-body">
        <p className="plant-hint">{t("onboarding.plant.people.blurb")}</p>

        {/* The bulk path: the same import pipeline as every other lane (§3.3). */}
        {canImportPeople && !frozen ? (
          <p className="plant-hint">
            {t("onboarding.plant.people.bulkHint")}{" "}
            <Link to="/onboarding/catalogue" className="ds-lnk">
              {t("onboarding.plant.people.bulkCta")}
            </Link>
          </p>
        ) : null}

        {forbidden ? (
          <p className="plant-hint" data-testid="people-forbidden">
            {t("onboarding.plant.people.notPermitted")}
          </p>
        ) : null}

        {canInvite ? (
          <form
            className="plant-invite-form"
            data-testid="plant-invite-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (!canSubmit) return;
              create.mutate(
                {
                  email: email.trim(),
                  role_id: roleId,
                  scope_kind: scope,
                  ...(scope === "facility" ? { facility_id: facilityId } : {}),
                },
                {
                  onSuccess: () => {
                    setEmail("");
                  },
                },
              );
            }}
          >
            <div className="ds-field">
              <label htmlFor="invite-email">{t("onboarding.plant.people.emailLabel")}</label>
              <input
                id="invite-email"
                data-testid="invite-email"
                className="ds-input"
                type="email"
                value={email}
                aria-invalid={email.length > 0 && !emailOk ? true : undefined}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
              />
            </div>
            <div className="ds-field">
              <label htmlFor="invite-role">{t("onboarding.plant.people.roleLabel")}</label>
              <select
                id="invite-role"
                data-testid="invite-role"
                className="ds-select"
                value={roleId}
                onChange={(e) => {
                  setRoleId(e.target.value);
                }}
              >
                <option value="">{t("onboarding.plant.people.rolePlaceholder")}</option>
                {/* A seeded system role's `name` IS an i18n key
                    (`roles.group_admin`), so this list offered raw dotted keys
                    as its choices until `roleLabel` landed (surface review
                    F5). A tenant's own custom role still renders as typed. */}
                {roleOptions.map((role) => (
                  <option key={role.id} value={role.id}>
                    {roleLabel(t, role.name)}
                  </option>
                ))}
              </select>
            </div>
            <div className="ds-field">
              <label htmlFor="invite-scope">{t("onboarding.plant.people.scopeLabel")}</label>
              <select
                id="invite-scope"
                data-testid="invite-scope"
                className="ds-select"
                value={scope}
                onChange={(e) => {
                  setScope(e.target.value === "facility" ? "facility" : "group");
                }}
              >
                <option value="group">{t("onboarding.plant.people.scopeGroup")}</option>
                <option value="facility">
                  {t("onboarding.plant.people.scopeFacility", { facility: term("facility") })}
                </option>
              </select>
            </div>
            {scope === "facility" ? (
              <div className="ds-field">
                <label htmlFor="invite-facility">
                  {t("onboarding.plant.people.facilityLabel", { facility: term("facility") })}
                </label>
                <select
                  id="invite-facility"
                  data-testid="invite-facility"
                  className="ds-select"
                  value={facilityId}
                  onChange={(e) => {
                    setFacilityId(e.target.value);
                  }}
                >
                  <option value="">{t("onboarding.plant.people.facilityPlaceholder")}</option>
                  {facilities.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name} ({f.code})
                    </option>
                  ))}
                </select>
                {facilities.length === 0 ? (
                  <p className="plant-hint">
                    {t("onboarding.plant.people.commitFirst", { facility: term("facility") })}
                  </p>
                ) : null}
              </div>
            ) : null}
            {/* Secondary on purpose: the plant screen's ONE primary is the
                commit (or the post-commit Continue) — §3.10. */}
            <Button
              type="submit"
              busy={create.isPending}
              disabled={!canSubmit}
              data-testid="invite-submit"
            >
              {create.isPending
                ? t("onboarding.plant.people.inviting")
                : t("onboarding.plant.people.invite")}
            </Button>
          </form>
        ) : null}

        {create.error !== null && !forbidden ? (
          <WizardProblemNotice
            error={create.error}
            onRetry={() => {
              create.reset();
            }}
            titleKey="onboarding.plant.people.inviteFailedTitle"
            detailKey="onboarding.plant.people.inviteFailedBody"
          />
        ) : null}

        {invites.error !== null && !forbidden ? (
          <ErrorState
            title={t("onboarding.plant.people.errorTitle")}
            detail={t("onboarding.plant.people.errorBody")}
            retryLabel={t("onboarding.state.retry")}
            onRetry={() => void invites.refetch()}
            requestId={requestIdOf(invites.error)}
            requestIdLabel={t("onboarding.state.requestId")}
          />
        ) : invites.isPending ? (
          <TableSkeleton rows={3} cols={4} label={t("onboarding.plant.people.loading")} />
        ) : (
          <DataTable
            columns={columns}
            rows={items}
            rowKey={(row) => row.id}
            caption={t("onboarding.plant.people.heading")}
            empty={
              <EmptyState
                title={t("onboarding.plant.people.emptyTitle")}
                body={t("onboarding.plant.people.emptyBody")}
              />
            }
          />
        )}
      </div>

      {/* Revoking cancels someone's access before they ever used it — confirmed,
          like every other destructive action in this module. */}
      {revoking !== null ? (
        <WizardDialog
          testId="invite-revoke-confirm"
          destructive
          title={t("onboarding.plant.people.revokeConfirmTitle")}
          confirmLabel={t("onboarding.plant.people.revoke")}
          cancelLabel={t("onboarding.plant.cancel")}
          busy={revoke.isPending}
          onConfirm={() => {
            revoke.mutate({ id: revoking.id });
            setRevoking(null);
          }}
          onCancel={() => {
            setRevoking(null);
          }}
        >
          <p>{t("onboarding.plant.people.revokeConfirmBody", { email: revoking.email })}</p>
        </WizardDialog>
      ) : null}
    </Card>
  );
}

function isForbidden(error: unknown): boolean {
  return error instanceof RequestError && error.httpStatus === 403;
}
